import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { Tooltip } from '@interstate/components/Tooltip';
import { InformationCircleIcon } from '@interstate/components/Icons';

import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';

import {
    ModalBody,
    SectionHeader,
    ScenarioList,
    ScenarioItem,
    VehicleLabel,
    InfoIcon,
    StyledModal
} from './CantBuildTemplateModal.style';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { ThemeProvider } from 'styled-components';
import { formatVehicleYMMT, formatPaymentDetails } from '../scenarioFormatUtils';

const TOOLTIP_TEXT = `To save this as a template, match the vehicle across all scenarios or remove scenarios you don't want in the template. You can also save the deal without creating a template.`;

export type CantBuildTemplateModalProps = {
    show: boolean;
    onHide: () => void;
};

const CantBuildTemplateModal: FC<CantBuildTemplateModalProps> = ({ show, onHide }) => {
    const theme = useInterstateTheme();
    const scenarioVehicles = useSelector(dealTemplateSelectors.getScenarioVehicleDetails);

    return (
        <ThemeProvider theme={theme}>
            <StyledModal
                size="large"
                id="cant-build-template-modal"
                data-testid="cant-build-template-modal"
                show={show}
                onHide={onHide}
                header={
                    <Box mt={0.5}>
                        <Typography variant="h3">{`Can't build template`}</Typography>
                    </Box>
                }
            >
                <ModalBody>
                    <Typography variant="body-md">
                        The deal has different vehicles. To build this deal as a template, all scenarios need to use the same
                        vehicle.
                    </Typography>
                    <SectionHeader>
                        <Typography variant="h5">Deal Scenarios</Typography>
                        <Tooltip toolTipContent={TOOLTIP_TEXT} size="large" position="bottom">
                            <InfoIcon>
                                <InformationCircleIcon />
                            </InfoIcon>
                        </Tooltip>
                    </SectionHeader>
                    <ScenarioList data-testid="cant-build-template-scenario-list">
                        {scenarioVehicles.map((detail, index) => (
                            <ScenarioItem
                                key={detail.scopeId}
                                data-testid={`cant-build-template-scenario-${index}`}
                                $isLast={index === scenarioVehicles.length - 1}
                            >
                                <VehicleLabel>
                                    <Typography variant="strong-sm" tag="span">
                                        {formatVehicleYMMT(detail)}
                                    </Typography>
                                    {detail.isPrimary && (
                                        <Badge variant="info" data-testid="cant-build-template-primary-badge">
                                            Primary
                                        </Badge>
                                    )}
                                </VehicleLabel>
                                {detail.offerType && (
                                    <Typography
                                        variant="body-sm"
                                        tag="span"
                                        color="sem.color.on-surface.muted"
                                        sx={{ whiteSpace: 'nowrap', ml: 2 }}
                                    >
                                        {formatPaymentDetails(detail)}
                                    </Typography>
                                )}
                            </ScenarioItem>
                        ))}
                    </ScenarioList>
                </ModalBody>
            </StyledModal>
        </ThemeProvider>
    );
};

export default CantBuildTemplateModal;
